import {ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import { SideProductsService } from '../../api/services/side-products.service';
import { ProfileDataRequest } from '../../api/models/perfil';
import { ProductResponse } from '../../api/services/side-products.service';
import {ShopItemModel} from "../../api/models/shop-item.model";
import {catchError, concat, finalize, mergeMap, of, Subscription, tap} from "rxjs";
import {CartService} from "../../api/services/cart.service";
import {SnackBarService} from "../../api/services/snack-bar.service";
import { DialogData, ProductBonusModalComponent } from '../product-bonus-modal/product-bonus-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { CheckoutService } from 'src/app/api/services/checkout.service';

@Component({
    selector: 'app-side-bar-products',
    templateUrl: './side-bar-products.component.html',
    styleUrls: ['./side-bar-products.component.css'],
    standalone: false
})
export class SideBarProductsComponent implements OnInit {
  activeTab: string = 'semanal';
  activeTabLab: string = 'sugestoes';
  @Output() sidebarToggle = new EventEmitter<boolean>();
  @Input() currentUser!: ProfileDataRequest;
  isSidebarOpen = true;
  @Input() product!: ShopItemModel;
  private productQueue: ShopItemModel[] = [];
  isProcessing = false;
  isCartButtonActive: boolean = true;
  unsubscribe: Subscription = new Subscription();

  products: ProductResponse = {
    semanal: [],
    mensal: [],
    quinzenal: [],
    sugestoes: []
  };

  @Output() isModalOpen = new EventEmitter<boolean>();

  isLoading: boolean = true;
  productsLoaded: boolean = false;
  productsQuantitiesLoaded: boolean = false;

  canClose: boolean = true;

  constructor(
    private productService: SideProductsService,
    private cartService: CartService,
    private snackBarService: SnackBarService,
    private cdr: ChangeDetectorRef,
    public dialog: MatDialog,
    private checkoutService: CheckoutService
  ) {
  }

  ngOnInit(): void {
    if (!this.currentUser || !this.currentUser.clientId) {
      return;
    }
    this.fetchProducts();
    this.GetCheckoutProducts(this.currentUser.clientId);
  }

  fetchProducts(): void {
    this.productService.getProducts(this.currentUser.clientId,this.currentUser.userId).subscribe({
      next: (response) => {
        this.products = response;
        this.productsLoaded=true;
        this.checkIfLoadingComplete();
      },
      error: (error) => {
        console.error('[Error Fetching Products]', error);
      },
    });
  }


  setActiveTab(tab: string) {
    this.activeTab = tab;
  }

  setActiveTabLab(tab: string) {
    this.activeTabLab = tab;
  }

  toggleSidebar() {
    // Check if there are products still being processed
    const hasPendingItems = this.productQueue.length > 0 || this.isProcessing;

    if (hasPendingItems) {
      this.snackBarService.openSnackBar(
        'Aguarde um momento enquanto finalizamos o processamento das operações realizadas.',
        '#FFA000',
        '../../assets/Icons/Warning-icon.png'
      );

      this.canClose = false;

      return; 
    }

    this.isSidebarOpen = !this.isSidebarOpen;
    this.sidebarToggle.emit(this.isSidebarOpen);
    this.unsubscribe.add(this.checkoutService
      .getUserCart(this.currentUser.userId,this.currentUser.clientId)
      .subscribe(response => this.cartService.updateShopCart(response)))
  }

  private notifyIfQueueFinished(): void {
    if (this.productQueue.length === 0 && !this.isProcessing) {
      this.snackBarService.openSnackBar(
        'Processamento das operações realizadas finalizado!',
        '#0AA174',
        '../../assets/Icons/correct.png'
      );
    }
  }


  removeQuantityClick(shopItem: ShopItemModel, category: keyof ProductResponse): void {
    if (!this.products[category]) return;
  
    this.products[category] = this.products[category].map(product =>
      product.reference === shopItem.reference
        ? { ...product, productQuantity: Math.max((product.productQuantity || 0) - 1, 0) }
        : product
    );
  
    this.cdr.detectChanges();
    this.productQueue.push(shopItem);
    this.startProcessingRemove();
  }

  startProcessingRemove(): void {
    if (this.isProcessing || this.productQueue.length === 0) return;

    this.isProcessing = true;
    const currentItem = this.productQueue.shift();

    if (!currentItem) {
      this.isProcessing = false;

      if(!this.canClose)
        this.notifyIfQueueFinished();

      return;
    }

    // Step 1: update local cart
    this.cartService.decreaseCartMethod(currentItem, !this.isCartButtonActive, false).subscribe({
      next: () => {
        // Step 2: update backend
        this.unsubscribe.add(
          this.cartService.decreaseCartMethod(currentItem, !this.isCartButtonActive, true).subscribe({
            next: (resp) => {
              if (resp) {
                this.snackBarService.openSnackBar(
                  'Produto removido do carrinho!',
                  '#0AA174',
                  '../../assets/Icons/correct.png'
                );
              }
              this.isProcessing = false;

              if(!this.canClose)
                this.notifyIfQueueFinished();

              this.startProcessingRemove(); // Process next in queue
            },
            error: () => {
              this.snackBarService.openSnackBar(
                'Erro ao remover produto!',
                '#E53935',
                '../../assets/Icons/error.png'
              );
              this.isProcessing = false;

              if(!this.canClose)
                this.notifyIfQueueFinished();
              
              this.startProcessingRemove();
            }
          })
        );
      },
      error: () => {
        this.snackBarService.openSnackBar(
          'Erro ao atualizar carrinho local!',
          '#E53935',
          '../../assets/Icons/error.png'
        );
        this.isProcessing = false;

        if(!this.canClose)
          this.notifyIfQueueFinished();
        
        this.startProcessingRemove();
      }
    });
  }


  addQuantityClick(shopItem: ShopItemModel, category: keyof ProductResponse): void {
    if (!this.products[category]) return; // Ensure category exists
  
    // Update the correct array
    this.products[category] = this.products[category].map(product =>
      product.reference === shopItem.reference
        ? { ...product, productQuantity: (product.productQuantity || 0) + 1 }
        : product
    );
  
    this.cdr.detectChanges();
    this.productQueue.push(shopItem);
    this.startProcessing();
  }  

  private startProcessing(): void {
    if (this.isProcessing || this.productQueue.length === 0) return;

    this.isProcessing = true;
    const currentItem = this.productQueue.shift();

    if (!currentItem) {
      this.isProcessing = false;

      if(!this.canClose)
        this.notifyIfQueueFinished();
        
      return;
    }

    // Step 1: Local cart state update
    this.cartService.increaseCartMethod(currentItem, !this.isCartButtonActive, false).subscribe({
      next: () => {
        // Step 2: Backend update
        this.unsubscribe.add(
          this.cartService.increaseCartMethod(currentItem, !this.isCartButtonActive, true).subscribe({
            next: (resp) => {
              if (resp) {
                this.snackBarService.openSnackBar(
                  'Produto adicionado ao carrinho!',
                  '#0AA174',
                  '../../assets/Icons/correct.png'
                );
              }
              this.isProcessing = false;

              if(!this.canClose)
                this.notifyIfQueueFinished();

              this.startProcessing(); // Proceed with next in queue
            },
            error: () => {
              this.snackBarService.openSnackBar(
                'Erro ao adicionar produto!',
                '#E53935',
                '../../assets/Icons/error.png'
              );
              this.isProcessing = false;

              if(!this.canClose)
                this.notifyIfQueueFinished();

              this.startProcessing();
            }
          })
        );
      },
      error: () => {
        this.snackBarService.openSnackBar(
          'Erro ao atualizar carrinho local!',
          '#E53935',
          '../../assets/Icons/error.png'
        );
        this.isProcessing = false;

        if(!this.canClose)
          this.notifyIfQueueFinished();
        
        this.startProcessing();
      }
    });
  }

  private finalizeUpdate(isAdding: boolean): void {
    this.isProcessing = true;
    const lastProduct = this.productQueue[this.productQueue.length - 1];
    if (lastProduct) {
      if (isAdding) {
        this.unsubscribe.add(
          this.cartService.increaseCartMethod(lastProduct, !this.isCartButtonActive, true).subscribe({
            next: (resp) => {
              if (resp) {
                this.snackBarService.openSnackBar(
                  'Produto adicionado ao carrinho!',
                  '#0AA174',
                  '../../assets/Icons/correct.png'
                );
              }
              this.isProcessing = false;
            }
          })
        );
      }
      else {
        this.unsubscribe.add(
          this.cartService
            .decreaseCartMethod(lastProduct, !this.isCartButtonActive, true)
            .subscribe((operation) => {
              setTimeout(() => {
                if (operation) {
                  this.snackBarService.openSnackBar(
                    'Produto removido do carrinho!',
                    '#0AA174',
                    '../../assets/Icons/correct.png'
                  );
                }
                this.isProcessing = false;
              }, 250);
            })
        );
      }
    }
  }

  isControlKey(event: KeyboardEvent): boolean {
    const controlKeys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Backspace', 'Delete', 'Tab'];
    return controlKeys.includes(event.key);
  }

  onEnterPress(inputElement: HTMLInputElement): void {
    inputElement.blur();
  }

  onKeyDown(event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement;
    const currentValue = input.value;
    if (+currentValue >= 1000 && !this.isControlKey(event)) {
      event.preventDefault();
    }
  }
  
  changeQuantity(
    newValue: string,
    shopItem: ShopItemModel,
    oldValue: number,
    category: keyof ProductResponse
  ): void {
    let numericValue = parseInt(newValue, 10);
  
    // Prevent invalid or unchanged values
    if (numericValue === oldValue || isNaN(numericValue) || numericValue < 0) {
      shopItem.productQuantity = oldValue;
      this.cdr.detectChanges();
      return;
    }
  
    // Ensure the category exists
    if (!this.products[category]) return;
  
    // Update the correct product in the list
    this.products[category] = this.products[category].map(product =>
      product.reference === shopItem.reference
        ? { ...product, productQuantity: numericValue }
        : product
    );
  
    this.cdr.detectChanges();
  
    // Send update request to cart service
    this.unsubscribe.add(
      this.cartService
        .changeQuantityCartMethod(shopItem, !this.isCartButtonActive, numericValue)
        .subscribe((operation) => {
          if (operation) {
            if (oldValue > 0 && numericValue > 0) {
              this.snackBarService.openSnackBar(
                'Quantidade do produto alterada no carrinho!',
                '#0AA174',
                '../../assets/Icons/correct.png'
              );
            }
            if (numericValue === 0) {
              this.snackBarService.openSnackBar(
                'Produto removido com sucesso!',
                '#0AA174',
                '../../assets/Icons/correct.png'
              );
            }
            if (oldValue === 0 && numericValue > 0) {
              this.snackBarService.openSnackBar(
                'Produto adicionado com sucesso!',
                '#0AA174',
                '../../assets/Icons/correct.png'
              );
            }
          }
        })
    );
  }
  

  addAllProductsToCart(products: ShopItemModel[], categoria: keyof ProductResponse): void {
    const addObservables = products.map((product) => {
        if (!this.products[categoria]) return of(false); // Ensure category exists

        // Replicating the logic from addQuantityClick method
        this.products[categoria] = this.products[categoria].map(p =>
            p.reference === product.reference
                ? { ...p, productQuantity: (p.productQuantity || 0) + 1 }
                : p
        );

        // Queue the product for processing
        this.productQueue.push(product);
        this.startProcessing();
        this.finalizeUpdate(true);

        return of(true); // Return success observable for each product
    });
    
    // Combine observables and handle them
    this.unsubscribe.add(
      concat(addObservables).pipe(
            catchError(() => {
                this.snackBarService.openSnackBar(
                    'Erro a adicionar lista de produtos',
                    '#F97066',
                    '../../assets/Icons/Warning-icon.png'
                );
                return of(false); // Return an observable in case of error
            }),
            finalize(() => {
                this.snackBarService.openSnackBar(
                    'Produtos adicionados com sucesso!',
                    '#0AA174',
                    '../../assets/Icons/correct.png'
                );
            })
        ).subscribe() // Subscribing to trigger the operation
    );
  }

  hasActiveQuantityCampaign(product: ShopItemModel): boolean {
    return product?.quantityCampaign?.some(campaign => 
      campaign.quantity >= 0 && campaign.discountPercentage > 0
    ) ?? false;
  }

  openProductBonus(product: ShopItemModel): void {
    this.isModalOpen.emit(true);
    
    var dataToSend: DialogData = {
      bonusData: product,
      addButton: true,
      processChanges: true
    };
    const dialogRef = this.dialog.open(ProductBonusModalComponent, {
        data: dataToSend
    });
    
    this.unsubscribe.add(
      dialogRef.afterClosed().subscribe((result) => {
        this.isModalOpen.emit(false);
        if (result?.reference && result?.quantityToAdd) {
          this.GetCheckoutProducts(this.currentUser.clientId);
        }
      })
    );
  }

  GetCheckoutProducts(sessionClientNumber: string): void {
    this.unsubscribe.add(
      this.checkoutService.GetCheckoutViewModel(sessionClientNumber).subscribe(
        (response) => {
          let currentCheckoutProducts = response.checkoutLines;
  
          // Iterate through each product in the checkout products
          currentCheckoutProducts.forEach((checkoutProduct) => {
            // Check for a matching product in the four categories
            Object.keys(this.products).forEach((categoryKey) => {
              const category = categoryKey as keyof ProductResponse;
              const matchedProduct = this.products[category].find(product => product.reference === checkoutProduct.referencia);
  
              // If a match is found, update the product's quantity
              if (matchedProduct) {
                matchedProduct.productQuantity = checkoutProduct.qtd; // Set the quantity to the value in the checkout line
              }
            });
          });
  
          this.cdr.detectChanges();

          this.productsQuantitiesLoaded=true;
          this.checkIfLoadingComplete();
        },
        (error) => {
          console.error('Error:', error);
        }
      )
    );
  }
  
  private checkIfLoadingComplete(): void {
    if (this.productsLoaded && this.productsQuantitiesLoaded) {
      this.isLoading = false;
      this.cdr.detectChanges();
    }
  }

}
